<template>
  <v-form ref="form" v-model="isFormValid" lazy-validation>
    <v-text-field
      v-model="form.title"
      :rules="formRules.title"
      :label="$t('lessonDialogPlaceholder')"
      outlined
      required
    ></v-text-field>

    <v-row class="lesson-create-row">
      <v-col>
        <date-picker
          :value="form.start.date"
          :rules="formRules.start.date"
          :label="$t('lessonDialogDate')"
          @change="updateDate"
        ></date-picker>
      </v-col>

      <v-col>
        <v-text-field
          v-model="form.start.time"
          :rules="formRules.start.time"
          :label="$t('lessonDialogTime')"
          v-mask="mask"
          type="time"
          :hint="$t('lessonDialogTimeHint')"
          outlined
          required
        ></v-text-field>
      </v-col>
    </v-row>
    <v-switch
      v-model="switchCustomInvite"
      class="switchInvite"
      :label="$t('iWillInvite')"
    ></v-switch>
    <v-layout v-if="!switchCustomInvite">
      <v-combobox
        multiple
        v-model="form.emails"
        v-on:input="limiter"
        :rules="formRules.emails"
        :label="$t('lessonDialogStudents')"
        :hint="$t('lessonDialogStudentsHint')"
        outlined
        append-icon=""
        :delimiters="[';', ' ']"
        chips
        class="tag-input"
        deletable-chips
        :search-input.sync="search"
        @keyup="paste(search)"
        @change="typed(search)"
      ></v-combobox>
    </v-layout>
    <v-alert
              :icon="switchCustomInvite ? 'mdi-link' : 'mdi-email'"
              class="text-start mt-4 lesson-alert"
              text
              type="success"
            >{{ switchCustomInvite ? `${$t('lessonDialogAboutLink')}` :
            `${$t('lessonDialogAbout')}` }}</v-alert>
    <v-btn
      color="primary"
      :loading="isLoading"
      large
      depressed
      rounded
      block
      class="createLessonButton"
      @click="submitForm"
    >{{ $t('lessonCreate') }}</v-btn>
  </v-form>
</template>

<script>
import DatePicker from '@/components/DatePicker.vue';
import { rtdb } from '@/db';
import { mask } from 'vue-the-mask';
import { mapGetters } from 'vuex';
import moment from 'moment';
import dropRight from 'lodash/dropRight';
import clone from 'lodash/clone';
import union from 'lodash/union';
import shortid from 'shortid';

export default {
  props: ['prefill'],
  components: {
    DatePicker,
  },
  directives: {
    mask,
  },
  created() {
    if (this.prefill) {
      this.form.title = this.prefill.title;
      this.form.emails = this.prefill.emails;
    }
  },
  data() {
    return {
      maxViewers: 35,
      isFormValid: true,
      prevEmails: '',
      mask: '##:##',
      currEmails: '',
      switchCustomInvite: false,
      items: [],
      search: '',
      form: {
        title: '',
        start: {
          date: moment(new Date()).format('YYYY-MM-DD'),
          time: '',
        },
        emails: [],
      },

      formRules: {
        title: [v => !!v || this.$t('alertLessonTitle')],
        start: {
          date: [v => !!v || this.$t('alertLessonDate')],
          time: [
            (v => !!v && !!this.validateTime(v)) || this.$t('alertLessonTime'),
          ],
        },
        emails: [v => v.length > 0 || this.$t('alertLessonEmail')],
      },

      isLoading: false,
    };
  },

  computed: {
    ...mapGetters(['auth']),
  },

  methods: {
    updateDate(value) {
      this.form.start.date = value;
    },
    validateTime(time) {
      let a = true;

      const timearr = time.split(':');
      if (timearr.length !== 2) {
        a = false;
      } else {
        if (!timearr[0] || !timearr[1]) {
          a = false;
        }
        // eslint-disable-next-line no-empty
        if (timearr[0] < 24 && timearr[1] < 60) {
        } else {
          a = false;
        }
      }
      return a;
    },
    limiter(e) {
      if (e.length > this.maxViewers) {
        e.pop();
      }
    },
    typed(search) {
      if (search !== null) {
        let searchValidation = '';
        searchValidation = this.validateEmails(search);
        if (searchValidation.length === 0) {
          this.form.emails = dropRight(this.form.emails, 1);
        }
      }
    },
    paste(search) {
      this.$nextTick(() => {
        if (search !== null && search.includes(',')) {
          // eslint-disable-next-line no-param-reassign
          let searchTrimmed = '';
          let searchValidation = '';
          searchTrimmed = search.replace(/\s/g, '');
          searchValidation = this.validateEmails(searchTrimmed);
          this.form.emails.push(...searchValidation);
          this.form.emails = union(this.form.emails);
          this.search = '';
        }
      });
    },

    validateEmails(emailsString) {
      const emailValidationRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+[.][a-zA-Z]{2,4}$/;

      return emailsString
        .replace(/\s+/g, '')
        .split(',')
        .filter(email => email.length && emailValidationRegex.test(email));
    },

    getRandomAnimoji() {
      const { animojis } = this.$store.state;
      return animojis[Math.floor(Math.random() * animojis.length)];
    },

    async submitForm() {
      const isValid = this.$refs.form.validate();

      if (!isValid) {
        return;
      }

      this.isLoading = true;

      const formData = {
        title: this.form.title,
        start: moment(
          `${this.form.start.date} ${this.form.start.time}`,
        ).toDate(),

        author: this.auth.email,
        status: 'draft',
        created: new Date(),

        windows: [
          {
            type: 'stream',
            participant: this.auth.email,
            camera: true,
          },
          {
            type: 'keynote',
            page: 0,
            link: '',
          },
        ],

        media: {
          links: [],
          files: [],
        },

        viewers: {},
      };

      let emails = [];
      if (!this.switchCustomInvite) {
        emails = clone(this.form.emails);
      }

      if (this.form.emails.length > this.maxViewers) {
        emails = dropRight(
          this.form.emails,
          this.form.emails.length - this.maxViewers,
        );
      }
      // eslint-disable-next-line key-spacing,quote-props
      const presenceMap = { '_d' : 0 };
      emails.forEach(email => {
        const key = shortid.generate();
        formData.viewers[key] = {
          email,
          waving: false,
          connected: false,
          voiceless: false,
          name: '',
          avatar: this.getRandomAnimoji(),
        };
        presenceMap[key] = false;
      });

      await this.$store
        .dispatch('addToDb', {
          type: 'lessons',
          value: formData,
        })
        .then(doc => {
          rtdb
            .ref(doc.id)
            .set(presenceMap)
            .then(() => {
              this.$store
                .dispatch('updateDb', {
                  type: 'lessons',
                  id: doc.id,
                  item: 'id',
                  value: doc.id,
                })
                .then(() => {
                  this.isLoading = false;
                  this.$emit('created', doc.id);
                  this.$store.dispatch('logEvent', {
                    name: 'lesson_created',
                  });
                });
            });
        });
    },
  },
};
</script>

<style lang="scss" scoped>

.lesson-create-row{
      margin-right: -12px !important;
    margin-left: -12px !important;
}
.v-alert--text {
  background: transparent !important;
}

.lesson-alert{
  margin-bottom:30px;
}

.switchInvite{
margin-top: 0px;
padding-left: 10px;
}
</style>
